/**
 * Displacement Settings Modal
 *
 * Small dark-theme modal (opened from the gear next to the DISP toggle in the
 * action bar) that edits ALL displacement strategy settings via the store's
 * updateSettings (GET/POST /api/displacement-settings). Save applies an
 * optimistic local merge and closes; Cancel discards the local draft.
 *
 * Reuses the Fair Price modal's stylesheet — the shell (overlay, header,
 * rows, footer) is identical and the classes are global, so duplicating the
 * CSS would only create drift.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useDisplacementStore,
  DisplacementSettings,
} from '../store/displacementStore';
import './FairPriceSettingsModal.css';

interface DisplacementSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type NumberKey =
  | 'rangeExpansionMult'
  | 'maxWickPct'
  | 'closeStrengthPct'
  | 'minRangePoints'
  | 'minRangePointsEs'
  | 'minRangePointsSil'
  | 'atrMult'
  | 'volumeMult'
  | 'volumeLookback'
  | 'athRangePoints'
  | 'weightExpansion'
  | 'weightCleanliness'
  | 'weightCloseStrength'
  | 'weightAtr'
  | 'weightVolume'
  | 'gradeAPlusScore'
  | 'gradeAScore'
  | 'followThroughPts'
  | 'failRetracePct'
  | 'maxHoldCandles'
  | 'lookbackCandles'
  | 'maxMarkers';

type ToggleKey =
  | 'enabled'
  | 'requireCloseBeyondPrev'
  | 'useAtrFilter'
  | 'useVolumeFilter'
  | 'athGuard'
  | 'detectFvg';

interface NumberField {
  key: NumberKey;
  label: string;
  step: number;
  min: number;
}

interface ToggleField {
  key: ToggleKey;
  label: string;
}

const SHAPE_FIELDS: NumberField[] = [
  { key: 'rangeExpansionMult', label: 'Range vs previous (x)', step: 0.05, min: 1 },
  { key: 'maxWickPct', label: 'Max total wick (% of range)', step: 1, min: 0 },
  { key: 'closeStrengthPct', label: 'Min close strength (%)', step: 1, min: 0 },
];

const SIZE_FIELDS: NumberField[] = [
  { key: 'minRangePoints', label: 'Min range — NQ / MNQ (points)', step: 0.25, min: 0 },
  { key: 'minRangePointsEs', label: 'Min range — ES / MES (points)', step: 0.25, min: 0 },
  { key: 'minRangePointsSil', label: 'Min range — SIL (points)', step: 0.01, min: 0 },
];

const GRADE_FIELDS: NumberField[] = [
  { key: 'weightExpansion', label: 'Weight — expansion', step: 0.05, min: 0 },
  { key: 'weightCleanliness', label: 'Weight — cleanliness (low wick)', step: 0.05, min: 0 },
  { key: 'weightCloseStrength', label: 'Weight — close strength', step: 0.05, min: 0 },
  { key: 'weightAtr', label: 'Weight — ATR ratio', step: 0.05, min: 0 },
  { key: 'weightVolume', label: 'Weight — volume ratio', step: 0.05, min: 0 },
  { key: 'gradeAPlusScore', label: 'A+ at score >=', step: 1, min: 0 },
  { key: 'gradeAScore', label: 'A at score >=', step: 1, min: 0 },
];

const LIFECYCLE_FIELDS: NumberField[] = [
  { key: 'followThroughPts', label: 'Confirm after follow-through (points)', step: 0.25, min: 0 },
  { key: 'failRetracePct', label: 'Fail on body retrace (%)', step: 5, min: 0 },
  { key: 'maxHoldCandles', label: 'Expire after (candles)', step: 1, min: 1 },
];

const HOUSEKEEPING_FIELDS: NumberField[] = [
  { key: 'lookbackCandles', label: 'Recalculate lookback (candles)', step: 50, min: 1 },
  { key: 'maxMarkers', label: 'Max stored markers', step: 10, min: 1 },
];

const GRADE_OPTIONS = ['A+', 'A', 'B'];

export const DisplacementSettingsModal: React.FC<DisplacementSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const settings = useDisplacementStore((s) => s.settings);
  const showFvg = useDisplacementStore((s) => s.showFvg);
  const [draft, setDraft] = useState<DisplacementSettings>(settings);
  const [isSaving, setIsSaving] = useState(false);
  const [recalcMessage, setRecalcMessage] = useState<string | null>(null);

  // Refresh from the engine and re-snapshot the draft each time we open.
  useEffect(() => {
    if (!isOpen) return;
    useDisplacementStore.getState().fetchSettings();
    setRecalcMessage(null);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setDraft(settings);
  }, [isOpen, settings]);

  if (!isOpen) return null;

  const setNumber = (key: NumberKey, raw: string): void => {
    const value = parseFloat(raw);
    setDraft((d) => ({ ...d, [key]: Number.isFinite(value) ? value : 0 }));
  };

  const setToggle = (key: ToggleKey, value: boolean): void => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await useDisplacementStore.getState().updateSettings(draft);
      // Thresholds changed, so historical markers no longer match the rules —
      // rebuild them rather than leaving a stale mix on the chart.
      await useDisplacementStore.getState().recalculate();
    } finally {
      setIsSaving(false);
    }
    onClose();
  };

  const handleRecalculate = async (): Promise<void> => {
    setRecalcMessage('Recalculating...');
    const count = await useDisplacementStore.getState().recalculate();
    setRecalcMessage(`${count} displacement candle${count === 1 ? '' : 's'} found`);
  };

  const renderNumberRow = (field: NumberField): React.ReactNode => (
    <label key={field.key} className="fp-settings-row">
      <span className="fp-settings-label">{field.label}</span>
      <input
        type="number"
        className="fp-settings-input"
        value={draft[field.key]}
        step={field.step}
        min={field.min}
        onChange={(e) => setNumber(field.key, e.target.value)}
      />
    </label>
  );

  const renderToggleRow = (field: ToggleField): React.ReactNode => (
    <label key={field.key} className="fp-settings-row fp-settings-toggle-row">
      <span className="fp-settings-label">{field.label}</span>
      <input
        type="checkbox"
        checked={draft[field.key]}
        onChange={(e) => setToggle(field.key, e.target.checked)}
      />
    </label>
  );

  return createPortal(
    <div className="fp-settings-overlay" onClick={onClose}>
      <div className="fp-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fp-settings-header">
          <h2>Displacement Settings</h2>
          <button className="fp-settings-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="fp-settings-content">
          <div className="fp-settings-section">
            <h3>Detection</h3>
            {renderToggleRow({ key: 'enabled', label: 'Enable displacement detection' })}
            {SHAPE_FIELDS.map(renderNumberRow)}
            {renderToggleRow({
              key: 'requireCloseBeyondPrev',
              label: 'Require close beyond previous candle',
            })}
          </div>

          <div className="fp-settings-section">
            <h3>Minimum Size</h3>
            {SIZE_FIELDS.map(renderNumberRow)}
            {renderToggleRow({ key: 'useAtrFilter', label: 'Also require an ATR multiple' })}
            <div className={draft.useAtrFilter ? '' : 'fp-settings-dimmed'}>
              {renderNumberRow({ key: 'atrMult', label: 'Range >= ATR x', step: 0.1, min: 0 })}
            </div>
          </div>

          <div className="fp-settings-section">
            <h3>Volume Confirmation</h3>
            {renderToggleRow({ key: 'useVolumeFilter', label: 'Require a volume surge' })}
            <div className={draft.useVolumeFilter ? '' : 'fp-settings-dimmed'}>
              {renderNumberRow({ key: 'volumeMult', label: 'Volume >= average x', step: 0.1, min: 0 })}
              {renderNumberRow({ key: 'volumeLookback', label: 'Average over (candles)', step: 1, min: 1 })}
            </div>
          </div>

          <div className="fp-settings-section">
            <h3>Grading</h3>
            <label className="fp-settings-row">
              <span className="fp-settings-label">Only show grade</span>
              <select
                className="fp-settings-input"
                value={draft.minGradeToEmit}
                onChange={(e) => setDraft((d) => ({ ...d, minGradeToEmit: e.target.value }))}
              >
                {GRADE_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {g} and above
                  </option>
                ))}
              </select>
            </label>
            {GRADE_FIELDS.map(renderNumberRow)}
          </div>

          <div className="fp-settings-section">
            <h3>Lifecycle</h3>
            {LIFECYCLE_FIELDS.map(renderNumberRow)}
          </div>

          <div className="fp-settings-section">
            <h3>Guards &amp; Display</h3>
            {renderToggleRow({
              key: 'athGuard',
              label: 'ATH guard (flag shorts near all-time high)',
            })}
            <div className={draft.athGuard ? '' : 'fp-settings-dimmed'}>
              {renderNumberRow({
                key: 'athRangePoints',
                label: 'Counts as "at the high" within (points)',
                step: 1,
                min: 0,
              })}
            </div>
            {renderToggleRow({ key: 'detectFvg', label: 'Detect the fair value gap left behind' })}
            <label className="fp-settings-row fp-settings-toggle-row">
              <span className="fp-settings-label">Draw the gap on the chart</span>
              <input
                type="checkbox"
                checked={showFvg}
                onChange={(e) => useDisplacementStore.getState().setShowFvg(e.target.checked)}
              />
            </label>
          </div>

          <div className="fp-settings-section">
            <h3>Housekeeping</h3>
            {HOUSEKEEPING_FIELDS.map(renderNumberRow)}
            <label className="fp-settings-row fp-settings-toggle-row">
              <span className="fp-settings-label">Rebuild markers from history</span>
              <button className="fp-settings-btn" onClick={handleRecalculate}>
                Recalculate
              </button>
            </label>
            {recalcMessage && <div className="fp-settings-label">{recalcMessage}</div>}
          </div>
        </div>

        <div className="fp-settings-footer">
          <button className="fp-settings-btn cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="fp-settings-btn save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DisplacementSettingsModal;
